import { tryCatch } from "@bcmi-labs/art-toolkit/utils"
import { forwardRef, useMergeRefs } from "@bcmi-labs/art-ui"
import { Image } from "@bcmi-labs/art-ui/components"
import { useDetectClosestTheme } from "@bcmi-labs/art-ui/theming"
import type { ComponentProps } from "react"
import { useMemo, useRef, useState } from "react"

import { resolveThemedAsset } from "@/lib/utils/assets"
import { staticAssets } from "@/static-assets"
import type { ThemedImageAssetPath } from "@/types/assets"

interface ThemedImageProps extends ComponentProps<typeof Image> {
  /**
   * The path to the base asset (without .dark suffix).
   * Component will automatically try to load the .dark variant in dark theme.
   *
   * @example "images/illustrations/generic-error.svg"
   * @example "images/covers/get-started.svg"
   */
  src: ThemedImageAssetPath
}

/**
 * Image component that automatically loads the correct asset based on the current theme.
 * Uses DOM to detect theme via data-art-theme attribute, staying in sync with CSS styling.
 *
 * Asset naming convention:
 * - Base asset (light/default): `images/illustrations/error.svg` (always required)
 * - Dark variant: `images/illustrations/error.dark.svg` (optional)
 *
 * In light theme: loads base asset
 * In dark theme: tries to load .dark variant, falls back to base if not found
 */
export const ThemedImage = forwardRef<HTMLSpanElement, ThemedImageProps>(({ src, loadingEvents, ...props }, ref) => {
  const { onError, ...otherLoadingEvents } = loadingEvents || {}

  const imgRef = useRef<HTMLSpanElement>(null)
  const detectedTheme = useDetectClosestTheme(imgRef)

  const mergedRef = useMergeRefs([ref, imgRef])

  const [hasError, setHasError] = useState(false)

  const image = useMemo(
    () =>
      tryCatch(() => ({
        baseUrl: staticAssets(src),
        imgSrc: resolveThemedAsset(src, detectedTheme)
      })),
    [src, detectedTheme]
  )

  return (
    <Image
      src={hasError ? image.data?.baseUrl || "" : image.data?.imgSrc || ""}
      loadingEvents={{
        onError: e => {
          onError?.(e)
          // Tries loading the base image if the themed one fails. If we're already on the base, we end up with the fallback.
          if (!hasError) setHasError(true)
        },
        ...otherLoadingEvents
      }}
      {...props}
      ref={mergedRef}
    />
  )
})
